/**
 * Site-wide SEO: title, description, canonical, Open Graph, Twitter, robots.
 * Blog posts use applyArticleSeo from BlogPostDetail; Layout uses applyDocumentSeo.
 */

import {
  DEFAULT_OG_IMAGE,
  DEFAULT_LOGO_URL,
  NOINDEX_PATH_PREFIXES,
  REGION_SEO_SLUGS,
  SEO_CONFIG,
} from '@/shared/config/seo.config'

export const SITE_URL = SEO_CONFIG.siteUrl

export type OgType = 'website' | 'article'

export type SeoMeta = {
  title: string
  description: string
  canonicalPath: string
  ogTitle?: string
  ogDescription?: string
  ogType?: OgType
  ogImage?: string
  ogImageAlt?: string
  twitterTitle?: string
  twitterDescription?: string
  twitterImage?: string
  twitterImageAlt?: string
  noindex?: boolean
  articlePublishedTime?: string
  articleModifiedTime?: string
}

type StaticRouteMeta = Pick<SeoMeta, 'title' | 'description' | 'noindex' | 'ogDescription' | 'twitterDescription'>

const STATIC_ROUTES: Record<string, StaticRouteMeta> = {
  '/': {
    title: SEO_CONFIG.defaultTitle,
    description: SEO_CONFIG.defaultDescription,
    ogDescription: SEO_CONFIG.defaultOgDescription,
    twitterDescription: SEO_CONFIG.defaultTwitterDescription,
  },
  '/shop': {
    title: 'Shop Travel eSIM Plans by Country | TTelGo',
    description:
      'Browse TTelGo prepaid travel eSIM plans by country and region. Compare data packages, instant QR activation, and coverage for your next trip.',
  },
  '/global-esim': {
    title: 'Global eSIM Plans | Multi-Country Travel Data | TTelGo',
    description:
      'TTelGo global and regional travel eSIM bundles for multiple destinations. Stay connected across borders with one prepaid data plan.',
  },
  '/about': {
    title: 'About TTelGo | International Travel eSIM Provider',
    description:
      'Learn about TTelGo, a travel eSIM brand by TikTel Ltd., offering prepaid international data and instant activation for travelers worldwide.',
  },
  '/download': {
    title: 'Download the TTelGo App | iOS & Android',
    description:
      'Download the TTelGo app to buy travel eSIM plans, manage orders, activate data, and get 24/7 support on iOS and Android.',
  },
  '/login': {
    title: 'Sign In | TTelGo',
    description: 'Log in to your TTelGo account to manage eSIM plans, orders, and profile settings.',
  },
  '/signup': {
    title: 'Create Account | TTelGo',
    description:
      'Create a TTelGo account to buy travel eSIM data, track orders, and manage your trips.',
  },
  '/profile': {
    title: 'My Profile | TTelGo',
    description: 'Update your TTelGo account profile and preferences.',
    noindex: true,
  },
  '/checkout': {
    title: 'Checkout | TTelGo',
    description: 'Complete your TTelGo travel eSIM purchase securely.',
    noindex: true,
  },
  '/my-esim': {
    title: 'My eSIM | TTelGo',
    description: 'View your TTelGo eSIM orders, QR codes, and activation status.',
    noindex: true,
  },
  '/help': {
    title: 'Help Centre | TTelGo Travel eSIM Support',
    description:
      'TTelGo Help Centre — FAQs, eSIM setup guides, billing help, and how to get support for your travel data plan.',
  },
  '/install-esim': {
    title: 'How to Install Your Travel eSIM | TTelGo',
    description:
      'Step-by-step TTelGo eSIM installation guide for iPhone and Android. Scan your QR code and activate travel data in minutes.',
  },
  '/contact': {
    title: 'Contact Us | TTelGo',
    description: 'Contact TTelGo for sales, support, or partnership questions about travel eSIM services.',
  },
  '/terms': {
    title: 'Terms & Conditions | TTelGo',
    description: 'TTelGo terms and conditions for using our website and travel eSIM services.',
  },
  '/privacy': {
    title: 'Privacy Policy | TTelGo',
    description: 'How TTelGo and TikTel Ltd. collect, use, and protect your personal data.',
  },
  '/faq': {
    title: 'Travel eSIM FAQ | TTelGo Help & Answers',
    description:
      'TTelGo eSIM FAQs: activation, device support, billing, refunds, and how prepaid travel eSIM works in 200+ destinations.',
  },
  '/blog': {
    title: 'Blog | TTelGo Travel eSIM Guides',
    description:
      'Travel eSIM guides, roaming savings tips, and connectivity advice from TTelGo.',
  },
  '/invite-friends': {
    title: 'Invite Friends | TTelGo',
    description: 'Share TTelGo travel eSIM with friends and stay connected together on your next trip.',
  },
  '/forgot-password': {
    title: 'Forgot Password | TTelGo',
    description: 'Reset your TTelGo account password.',
    noindex: true,
  },
  '/reset-password': {
    title: 'Reset Password | TTelGo',
    description: 'Choose a new password for your TTelGo account.',
    noindex: true,
  },
}

function humanizePathSegment(segment: string): string {
  return decodeURIComponent(segment)
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

/** Normalize pathname for canonical URLs (no query/hash, no trailing slash except root). */
export function normalizeCanonicalPath(pathname: string): string {
  let path = pathname.split('?')[0]?.split('#')[0] ?? '/'
  if (!path.startsWith('/')) path = `/${path}`
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1)
  return path
}

export function buildCanonicalUrl(pathname: string): string {
  const path = normalizeCanonicalPath(pathname)
  return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`
}

export function resolveAbsoluteImageUrl(imagePath?: string | null): string {
  if (!imagePath?.trim()) return DEFAULT_OG_IMAGE
  if (/^https?:\/\//i.test(imagePath)) {
    return imagePath.replace(/\/images\/ttelgo-og\.jpg$/i, '/IMAGES/ttelgo-og.jpg')
  }
  const normalized = imagePath.startsWith('/') ? imagePath : `/${imagePath}`
  const cased = normalized.replace(/^\/images\/ttelgo-og\.jpg$/i, '/IMAGES/ttelgo-og.jpg')
  return `${SITE_URL}${cased}`
}

export function shouldNoindexPath(pathname: string): boolean {
  const path = normalizeCanonicalPath(pathname)
  return NOINDEX_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
}

function countrySeo(label: string): SeoMeta {
  const path = `/country/${encodeURIComponent(label)}`
  return {
    title: `${label} eSIM | Prepaid Travel Data | TTelGo`,
    description: `Get a TTelGo eSIM for ${label} and stay connected with convenient prepaid mobile data. Buy online and activate your eSIM quickly.`,
    canonicalPath: path,
    ogTitle: `${label} eSIM | TTelGo`,
    ogDescription: `Prepaid travel eSIM data for ${label}. Instant activation with TTelGo — no physical SIM required.`,
  }
}

function regionSeo(label: string): SeoMeta {
  const path = `/region/${encodeURIComponent(label)}`
  return {
    title: `${label} eSIM Plans | Prepaid Regional Data | TTelGo`,
    description: `Browse TTelGo regional eSIM plans for ${label}. Prepaid travel data across multiple countries with instant activation.`,
    canonicalPath: path,
    ogTitle: `${label} eSIM | TTelGo`,
    ogDescription: `Regional prepaid eSIM data for ${label}. Explore TTelGo plans and stay connected while traveling.`,
  }
}

/**
 * Resolve SEO metadata for a pathname. Returns null when BlogPostDetail owns the head.
 */
export function resolveSeoMeta(pathname: string): SeoMeta | null {
  const path = normalizeCanonicalPath(pathname)

  if (/^\/blog\/[^/]+/.test(path)) return null

  if (STATIC_ROUTES[path]) {
    const route = STATIC_ROUTES[path]
    return {
      title: route.title,
      description: route.description,
      canonicalPath: path,
      ogDescription: route.ogDescription,
      twitterDescription: route.twitterDescription,
      noindex: route.noindex ?? shouldNoindexPath(path),
    }
  }

  if (path.startsWith('/region/')) {
    const label = humanizePathSegment(path.slice('/region/'.length))
    return { ...regionSeo(label), noindex: false }
  }

  if (path.startsWith('/country/')) {
    const label = humanizePathSegment(path.slice('/country/'.length))
    return { ...countrySeo(label), noindex: false }
  }

  return {
    title: SEO_CONFIG.defaultTitle,
    description: SEO_CONFIG.defaultDescription,
    canonicalPath: path,
    ogDescription: SEO_CONFIG.defaultOgDescription,
    twitterDescription: SEO_CONFIG.defaultTwitterDescription,
    noindex: shouldNoindexPath(path),
  }
}

/** @deprecated Use resolveSeoMeta — kept for tests and gradual migration. */
export function getLayoutSeo(pathname: string): { title: string; description: string } | 'skip' {
  const meta = resolveSeoMeta(pathname)
  if (meta === null) return 'skip'
  return { title: meta.title, description: meta.description }
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function removeMeta(attr: 'name' | 'property', key: string) {
  document.querySelector(`meta[${attr}="${key}"]`)?.remove()
}

function setCanonical(href: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.rel = 'canonical'
    document.head.appendChild(link)
  }
  link.href = href
}

function setRobots(noindex: boolean) {
  if (noindex) {
    setMeta('name', 'robots', 'noindex, nofollow')
  } else {
    removeMeta('name', 'robots')
  }
}

function upsertJsonLd(id: string, payload: Record<string, unknown>) {
  document.getElementById(id)?.remove()
  const script = document.createElement('script')
  script.id = id
  script.type = 'application/ld+json'
  script.textContent = JSON.stringify(payload)
  document.head.appendChild(script)
}

export function applySeoMeta(meta: SeoMeta): void {
  const canonical = buildCanonicalUrl(meta.canonicalPath)
  const ogTitle = meta.ogTitle ?? meta.title
  const ogDescription = meta.ogDescription ?? meta.description
  const twitterTitle = meta.twitterTitle ?? ogTitle
  const twitterDescription = meta.twitterDescription ?? ogDescription
  const ogImage = resolveAbsoluteImageUrl(meta.ogImage)
  const ogImageAlt = meta.ogImageAlt ?? SEO_CONFIG.defaultOgImageAlt
  const twitterImage = resolveAbsoluteImageUrl(meta.twitterImage ?? meta.ogImage)
  const twitterImageAlt = meta.twitterImageAlt ?? ogImageAlt
  const ogType = meta.ogType ?? 'website'

  document.title = meta.title
  setMeta('name', 'description', meta.description)
  setCanonical(canonical)
  setRobots(meta.noindex ?? false)

  setMeta('property', 'og:type', ogType)
  setMeta('property', 'og:site_name', SEO_CONFIG.siteName)
  setMeta('property', 'og:title', ogTitle)
  setMeta('property', 'og:description', ogDescription)
  setMeta('property', 'og:url', canonical)
  setMeta('property', 'og:image', ogImage)
  setMeta('property', 'og:image:width', String(SEO_CONFIG.ogImageWidth))
  setMeta('property', 'og:image:height', String(SEO_CONFIG.ogImageHeight))
  setMeta('property', 'og:image:alt', ogImageAlt)
  setMeta('property', 'og:locale', SEO_CONFIG.locale)

  if (ogType === 'article' && meta.articlePublishedTime) {
    setMeta('property', 'article:published_time', meta.articlePublishedTime)
  } else {
    removeMeta('property', 'article:published_time')
  }
  if (ogType === 'article' && meta.articleModifiedTime) {
    setMeta('property', 'article:modified_time', meta.articleModifiedTime)
  } else {
    removeMeta('property', 'article:modified_time')
  }

  setMeta('name', 'twitter:card', 'summary_large_image')
  setMeta('name', 'twitter:site', SEO_CONFIG.twitterSite)
  setMeta('name', 'twitter:title', twitterTitle)
  setMeta('name', 'twitter:description', twitterDescription)
  setMeta('name', 'twitter:image', twitterImage)
  setMeta('name', 'twitter:image:alt', twitterImageAlt)
}

export function applyDocumentSeo(pathname: string): void {
  const meta = resolveSeoMeta(pathname)
  if (meta === null) return
  applySeoMeta(meta)

  if (normalizeCanonicalPath(pathname) === '/download') {
    upsertJsonLd('ttelgo-mobile-app-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'MobileApplication',
      name: 'TTelGo',
      operatingSystem: 'iOS, Android',
      applicationCategory: 'TravelApplication',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      downloadUrl: [SEO_CONFIG.appStoreUrl, SEO_CONFIG.playStoreUrl],
      publisher: { '@type': 'Organization', name: SEO_CONFIG.siteName, url: `${SITE_URL}/` },
    })
  } else {
    document.getElementById('ttelgo-mobile-app-jsonld')?.remove()
  }
}

export function applyArticleSeo(input: {
  title: string
  description: string
  canonicalPath: string
  featuredImage?: string | null
  publishedAt?: string | null
  updatedAt?: string | null
  imageAlt?: string
}): () => void {
  const meta: SeoMeta = {
    title: input.title,
    description: input.description,
    canonicalPath: input.canonicalPath,
    ogTitle: input.title,
    ogDescription: input.description,
    ogType: 'article',
    ogImage: input.featuredImage ?? undefined,
    ogImageAlt: input.imageAlt ?? input.title,
    twitterImage: input.featuredImage ?? undefined,
    twitterImageAlt: input.imageAlt ?? input.title,
    articlePublishedTime: input.publishedAt ?? undefined,
    articleModifiedTime: input.updatedAt ?? input.publishedAt ?? undefined,
    noindex: false,
  }

  applySeoMeta(meta)

  const canonical = buildCanonicalUrl(input.canonicalPath)
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: input.title,
    description: input.description,
    image: resolveAbsoluteImageUrl(input.featuredImage),
    datePublished: input.publishedAt ?? undefined,
    dateModified: input.updatedAt ?? input.publishedAt ?? undefined,
    author: { '@type': 'Organization', name: 'TTelGo' },
    publisher: {
      '@type': 'Organization',
      name: 'TTelGo',
      logo: { '@type': 'ImageObject', url: DEFAULT_LOGO_URL },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
  }

  upsertJsonLd('ttelgo-blogpost-jsonld', schema)

  return () => {
    document.getElementById('ttelgo-blogpost-jsonld')?.remove()
  }
}

/** Country paths for sitemap/prerender (mirrors countryPathName aliases). */
export function countryPathFromDisplayName(displayName: string): string {
  const aliases: Record<string, string> = {
    UAE: 'United Arab Emirates',
    UK: 'United Kingdom',
  }
  return aliases[displayName] ?? displayName
}

export function listRegionSeoPaths(): string[] {
  return REGION_SEO_SLUGS.map((label) => `/region/${encodeURIComponent(label)}`)
}
